import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { JSX } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { PrimaryBold } from "@ui/components/containers/primary";
import { PlusIconRound, RemoveIconRound, TemplateFileIcon } from "@ui/components/icons";
import { InfoBulle } from "@ui/components/infoBulle";
import { RadioButton } from "@ui/components/inputs/radio";
import { row, stack } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { theme } from "@ui/styles/theme";
import { alpha, mobile } from "@ui/styles/utils";
import { useApplicationEditorController } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";
import { FileReminderInfo } from "@ui/pages/application/applicationEditorPage/fileReminderInfo";

const self_declaration_url = "https://docs.google.com/document/d/1YioEfSQBrRFn62bJxklmgTw4VI2R3isbbss4LzqUgCc";
const work_experience_url = "https://drive.google.com/file/d/131AEBzAMahgSx0_muBDgpJYJK7SNT1e5/view?usp=sharing";
const list_degree_url = "https://drive.google.com/file/d/1GYTGP0MbW_kR3JKYdCgfBJtsle5TD7ZL/view?usp=sharing";
export const DocumentSectionEditor: React.FC = ({ ...htmlProps }) => {
	const controller = useApplicationEditorController();
	const {
		idPassport,
		proofResidence,
		selfDeclarationFinancialNeed,
		resume,
		englishCertificat,
		isCertificate,
		diploma,
		transcriptRecord,
		workCertificate,
	} = useObservable(controller.application);

	const getFormItem = (
		field:
			| "identity"
			| "residency"
			| "financial_declaration"
			| "resume"
			| "english_certificate"
			| "diploma"
			| "transcript_diploma"
			| "work_certificate"
	): JSX.Element => (
		<InfoEdition>
			<PrimaryBold>
				{I18n.t(`application.form.${field}.label`)}{" "}
				{field === "english_certificate" ||
				field === "transcript_diploma" ||
				field === "work_certificate" ? undefined : (
					<span style={{ color: "red" }}>*</span>
				)}
			</PrimaryBold>
			{field === "diploma" || field === "work_certificate" ? undefined : (
				<InfoBulle text={I18n.t(`application.form.${field}.tooltip`)} />
			)}
		</InfoEdition>
	);

	return (
		<Container {...htmlProps}>
			<FormField>
				{getFormItem("identity")}
				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod
					type="document"
					onUpload={(media) => {
						controller.setIndentity(media);
					}}
				>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.identity.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{idPassport && (
					<StyledFileInfo>
						{idPassport?.displayName} <RemoveIconRound onClick={() => controller.removeIdentity()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("residency")}
				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod type="document" onUpload={(media) => controller.setResidency(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.residency.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{proofResidence && (
					<StyledFileInfo>
						{proofResidence?.displayName}
						<RemoveIconRound onClick={() => controller.removeResidency()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("diploma")}
				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod type="document" onUpload={(media) => controller.setDiploma(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.diploma.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{diploma && (
					<StyledFileInfo>
						{diploma?.displayName}
						<RemoveIconRound onClick={() => controller.removeDiploma()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("transcript_diploma")}
				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod type="document" onUpload={(media) => controller.setTranscriptRecord(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.transcript_diploma.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{transcriptRecord && (
					<StyledFileInfo>
						{transcriptRecord?.displayName}
						<RemoveIconRound onClick={() => controller.removeTranscriptRecord()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("work_certificate")}
				<StyledTemplateWorkCertificate>
					<LinkExternal to={work_experience_url}>
						<StyledTemplateFileLabel>
							<TemplateFileIcon />
							<TemplateFileLabel>{I18n.t("application.form.work_certificate_template_file.label")}</TemplateFileLabel>
						</StyledTemplateFileLabel>
					</LinkExternal>
					<LinkExternal to={list_degree_url}>
						<StyledTemplateFileLabel marginLeft={50}>
							<TemplateFileIcon />
							<TemplateFileLabel>{I18n.t("application.form.related_degrees_file.label")}</TemplateFileLabel>
						</StyledTemplateFileLabel>
					</LinkExternal>
				</StyledTemplateWorkCertificate>

				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod type="document" onUpload={(media) => controller.setWorkCertificate(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.work_certificate.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{workCertificate && (
					<StyledFileInfo>
						{workCertificate?.displayName}
						<RemoveIconRound onClick={() => controller.removeWorkCertificate()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("resume")}
				<FileReminderInfo fileSize={2} acceptedFormat="pdf" />
				<StyledFileUplaod type="pdf" onUpload={(media) => controller.setResume(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.resume.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{resume && (
					<StyledFileInfo>
						{resume?.displayName}
						<RemoveIconRound onClick={() => controller.removeResume()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("financial_declaration")}
				<LinkExternal to={self_declaration_url}>
					<StyledTemplateFileLabel>
						<TemplateFileIcon />
						<TemplateFileLabel>{I18n.t("application.form.template_file.label")}</TemplateFileLabel>
					</StyledTemplateFileLabel>
				</LinkExternal>
				<FileReminderInfo fileSize={2} acceptedFormat="document" />
				<StyledFileUplaod type="document" onUpload={(media) => controller.setFinancialInformation(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.financial_declaration.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{selfDeclarationFinancialNeed && (
					<StyledFileInfo>
						{selfDeclarationFinancialNeed?.displayName}
						<RemoveIconRound onClick={() => controller.removeFinancialInformation()} />
					</StyledFileInfo>
				)}
			</FormField>
			<FormField>
				{getFormItem("english_certificate")}
				<RadioButton
					checkbox
					label={I18n.t("application.form.english_certificate.checkbox")}
					id="englishcertificate"
					name="english_certificate"
					checked={isCertificate ?? false}
					onChange={() => controller.setIsCertificate(!isCertificate)}
				/>
				{isCertificate && (
					<>
						<FileReminderInfo fileSize={2} acceptedFormat="document" />
						<StyledFileUplaod type="document" onUpload={(media) => controller.setEnglisCertificat(media)}>
							{(_, loading) => (
								<div className="fileUpload">
									<StyledPlaceholder>{I18n.t("application.form.english_certificate.placeholder")}</StyledPlaceholder>
									{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
								</div>
							)}
						</StyledFileUplaod>
						{englishCertificat && (
							<StyledFileInfo>
								{englishCertificat?.displayName}
								<RemoveIconRound onClick={() => controller.removeEnglisCertificat()} />
							</StyledFileInfo>
						)}
						{/* <h6 style={{ fontWeight: 500, fontSize: "12px" }}>
							{I18n.t("application.form.english_certificate.description")}
						</h6> */}
					</>
				)}
			</FormField>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin-top: 100px;
	@media ${mobile} {
		margin-top: 180px;
	}
`;

const InfoEdition = styled.div`
	${row(10, "flex-start", "stretch")};
`;

const FormField = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 70%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledFileUplaod = styled(FileUpload)`
	.fileUpload {
		display: flex;
		align-items: center;
		border-radius: 7px;
		align-content: flex-end;
		justify-content: space-between;
		padding: 11px 15px;
		background-color: rgba(243, 244, 246, 0.4);
		border: 1.5px solid #e9e9e9;
	}
`;

// const StyledFilesContainer = styled.div`
// 	display: grid;
// 	grid-template-columns: repeat(2, 1fr);
// 	grid-gap: 20px;

// 	@media ${mobile} {
// 		display: flex;
// 		flex-direction: column;
// 	}
// `;

const StyledFileInfo = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: ${alpha("#BEDAFF", 0.5)};
	padding: 10px;
	font-size: 14px;
	border-radius: 7px;
`;

const StyledPlaceholder = styled.div`
	color: #8b8b8b;
	font-size: 14px;
	font-weight: 500;
`;

const StyledTemplateWorkCertificate = styled.div`
	display: flex;
	flex-direction: row;
`;

const StyledTemplateFileLabel = styled.h6<{ marginLeft?: number }>`
	display: flex;
	flex-direction: row;
	font-weight: 500;
	font-size: 12px;
	color: ${theme.colors.secondary};
	margin-left: ${({ marginLeft }) => marginLeft && `${marginLeft}px`};
`;

const TemplateFileLabel = styled.div`
	margin-left: 5px;
`;
